import { WiseOwlElementIdsType } from "../constants";
import { WiseOwlUser } from "./user";
import { ValueOf } from "./utility";
import { WiseOwlApplicationEnumType, WiseOwlIngress } from "./wiseOwlApi";
import { LiveChatConfiguration } from "./liveChat";
import { ZendeskSidebarTicket } from "./wiseOwlZendesk";
import { WiseOwlContextProps } from "./wiseOwlContext";

/**
 * EVENT TYPES
 */
export const WiseOwlEventEnum = {
  createConversation: "createConversation",
  messageDone: "messageDone",
  sendMessage: "sendMessage",
  csatSurveyCanceled: "csatSurveyCanceled",
  csatSurveySubmitted: "csatSurveySubmitted",
  getChat: "getChat",
  getChatByAboutTicketId: "getChatByAboutTicketId",
  getChatSummary: "getChatSummary",
  getCsatSurveyQuestions: "getCsatSurveyQuestions",
  getSurveyResponsesForLiveChat: "getSurveyResponsesForLiveChat",
  getPreChatContent: "getPreChatContent",
  liveChatTransfer: "liveChatTransfer",
  loginRedirect: "loginRedirect",
  openCsatSurvey: "openCsatSurvey",
  phoneSupport: "phoneSupport",
  redirect: "redirect",
  sendChatMessage: "sendChatMessage",
  sendChatMessageRetry: "sendChatMessageRetry",
  startNewChat: "startNewChat",
  startNewChatRetry: "startNewChatRetry",
  streamComplete: "streamComplete",
  streamError: "streamError",
  streamInitialize: "streamInitialize",
  submitFeedback: "submitFeedback",
  submitNewChat: "submitNewChat",
  submitTicket: "submitTicket",
  chatButtonToolClick: "chatButtonToolClick",
  displayPromptLibrary: "displayPromptLibrary",
  getHcaInsights: "getHcaHomeInsights",
};

export type WiseOwlEventEnumType = ValueOf<typeof WiseOwlEventEnum>;

export interface BaseEventData {
  chatId?: string;
}

export interface AboutTicketIdEventData extends BaseEventData {
  aboutTicketId: number;
  // Never
  redirectUrl?: never;
  retryId?: never;
  chatToolType?: never;
  syncDocumentSid?: never;
  messageId?: never;
  positive?: never;
  resumeChat?: boolean;
  liveChatId?: never;
  isLiveChatActive?: never;
}

export interface ChatEventData extends BaseEventData {
  aboutTicketId?: never;
  resumeChat?: boolean;
  isLiveChatActive?: boolean;
  liveChatId?: string;
  chatToolType?: string;
  displayPrompt?: string;
  //Never
  redirectUrl?: never;
  retryId?: never;
  syncDocumentSid?: never;
  messageId?: never;
  positive?: never;
}

export interface SyncDocumentEventData extends BaseEventData {
  syncDocumentSid: string;
  // Never
  aboutTicketId?: never;
  messageId?: never;
  positive?: never;
  redirectUrl?: never;
  retryId?: never;
  resumeChat?: never;
  liveChatId?: never;
  chatToolType?: never;
  isLiveChatActive?: never;
}

export interface RedirectUrlEventData extends BaseEventData {
  redirectUrl: string;
  // Never
  aboutTicketId?: never;
  messageId?: never;
  positive?: never;
  retryId?: never;
  syncDocumentSid?: never;
  resumeChat?: never;
  liveChatId?: never;
  chatToolType?: never;
  isLiveChatActive?: never;
}

export interface RetryEventData extends BaseEventData {
  retryId: string;
  // Nevers
  aboutTicketId?: never;
  messageId?: never;
  positive?: never;
  redirectUrl?: never;
  syncDocumentSid?: never;
  resumeChat?: never;
  liveChatId?: never;
  isLiveChatActive?: never;
  chatToolType?: never;
}

export interface FeedbackEventData extends BaseEventData {
  messageId: string;
  positive: string;
  // Nevers
  aboutTicketId?: never;
  redirectUrl?: never;
  retryId?: never;
  syncDocumentSid?: never;
  resumeChat?: never;
  liveChatId?: never;
  chatToolType?: never;
  isLiveChatActive?: never;
}

export interface GetSubmittedSurveysForLiveChatEventData extends BaseEventData {
  liveChatId: string;
  isLiveChatActive?: boolean;
  // Nevers
  messageId?: never;
  positive?: never;
  redirectUrl?: never;
  retryId?: never;
  syncDocumentSid?: never;
  chatToolType?: never;
  resumeChat?: never;
}

/**
 * EVENT RESPONSE TYPES
 */
export type OnEventResponse =
  | OnEventResponseGetChat
  | OnEventResponseGetChatByAboutTicketId
  | OnEventResponseGetChatSummary
  | OnEventResponseLiveChat
  | OnEventResponseLoginRedirect
  | OnEventResponsePhoneSupport
  | OnEventResponseRedirect
  | OnEventResponseSendChatMessage
  | OnEventResponseSendChatMessageRetry
  | OnEventResponseStartNewChat
  | OnEventResponseStartNewChatRetry
  | OnEventResponseStreamComplete
  | OnEventResponseStreamError
  | OnEventResponseStreamInitialize
  | OnEventResponseSubmitFeedback
  | OnEventResponseSubmitTicket;

export interface OnEventResponseGetChat {
  eventType: typeof WiseOwlEventEnum.getChat;
  data: ChatEventData;
}

export interface OnEventResponseGetChatByAboutTicketId {
  eventType: typeof WiseOwlEventEnum.getChatByAboutTicketId;
  data: AboutTicketIdEventData;
}

export interface OnEventResponseGetChatSummary {
  eventType: typeof WiseOwlEventEnum.getChatSummary;
  data: ChatEventData;
}

// TODO: Update with live chat changes
export interface OnEventResponseLiveChat {
  eventType: typeof WiseOwlEventEnum.liveChatTransfer;
  data: ChatEventData;
}

export interface OnEventResponseLoginRedirect {
  eventType: typeof WiseOwlEventEnum.loginRedirect;
  data: ChatEventData;
}

// TODO: Update with phone support changes
export interface OnEventResponsePhoneSupport {
  eventType: typeof WiseOwlEventEnum.phoneSupport;
  data: ChatEventData;
}

export interface OnEventResponseRedirect {
  eventType: typeof WiseOwlEventEnum.redirect;
  data: RedirectUrlEventData;
}

export interface OnEventResponseSendChatMessage {
  eventType: typeof WiseOwlEventEnum.sendChatMessage;
  data: SyncDocumentEventData;
}

export interface OnEventResponseSendChatMessageRetry {
  eventType: typeof WiseOwlEventEnum.sendChatMessageRetry;
  data: RetryEventData;
}

export interface OnEventResponseStartNewChat {
  eventType: typeof WiseOwlEventEnum.startNewChat;
  data: SyncDocumentEventData;
}

export interface OnEventResponseStartNewChatRetry {
  eventType: typeof WiseOwlEventEnum.startNewChatRetry;
  data: RetryEventData;
}

export interface OnEventResponseStreamComplete {
  eventType: typeof WiseOwlEventEnum.streamComplete;
  data: ChatEventData;
}

export interface OnEventResponseStreamError {
  eventType: typeof WiseOwlEventEnum.streamError;
  data: ChatEventData;
}

export interface OnEventResponseStreamInitialize {
  eventType: typeof WiseOwlEventEnum.streamInitialize;
  data: ChatEventData;
}

export interface OnEventResponseSubmitFeedback {
  eventType: typeof WiseOwlEventEnum.submitFeedback;
  data: FeedbackEventData;
}

export interface OnEventResponseSubmitTicket {
  eventType: typeof WiseOwlEventEnum.submitTicket;
  data: ChatEventData;
}

export interface OnEventResponseGetSurveyResponsesForLiveChat {
  eventType: typeof WiseOwlEventEnum.getSurveyResponsesForLiveChat;
  data: GetSubmittedSurveysForLiveChatEventData;
}

/**
 * ERROR RESPONSE TYPES
 */
export type OnErrorResponse =
  | OnErrorResponseGetChatSummary
  | OnErrorResponseGetChatByAboutTicketId
  | OnErrorResponseLiveChat
  | OnErrorResponseLoginRedirect
  | OnErrorResponsePhoneSupport
  | OnErrorResponseRedirect
  | OnErrorResponseSendChatMessage
  | OnErrorResponseSendChatMessageRetry
  | OnErrorResponseStartNewChat
  | OnErrorResponseStartNewChatRetry
  | OnErrorResponseStreamComplete
  | OnErrorResponseStreamError
  | OnErrorResponseStreamInitialize
  | OnErrorResponseSubmitFeedback
  | OnErrorResponseSubmitTicket
  | OnErrorResponseGetSurveyResponsesForLiveChat;

export interface OnErrorResponseBase {
  error: Error;
}

export type OnErrorResponseGetChatByAboutTicketId = OnErrorResponseBase &
  OnEventResponseGetChatByAboutTicketId;
export type OnErrorResponseGetChatSummary = OnErrorResponseBase &
  OnEventResponseGetChatSummary;
export type OnErrorResponseLiveChat = OnErrorResponseBase &
  OnEventResponseLiveChat;
export type OnErrorResponseLoginRedirect = OnErrorResponseBase &
  OnEventResponseLoginRedirect;
export type OnErrorResponsePhoneSupport = OnErrorResponseBase &
  OnEventResponsePhoneSupport;
export type OnErrorResponseRedirect = OnErrorResponseBase &
  OnEventResponseRedirect;
export type OnErrorResponseSendChatMessage = OnErrorResponseBase &
  OnEventResponseSendChatMessage;
export type OnErrorResponseSendChatMessageRetry = OnErrorResponseBase &
  OnEventResponseSendChatMessageRetry;
export type OnErrorResponseStartNewChat = OnErrorResponseBase &
  OnEventResponseStartNewChat;
export type OnErrorResponseStartNewChatRetry = OnErrorResponseBase &
  OnEventResponseStartNewChatRetry;
export type OnErrorResponseStreamComplete = OnErrorResponseBase &
  OnEventResponseStreamComplete;
export type OnErrorResponseStreamError = OnErrorResponseBase &
  OnEventResponseStreamError;
export type OnErrorResponseStreamInitialize = OnErrorResponseBase &
  OnEventResponseStreamInitialize;
export type OnErrorResponseSubmitFeedback = OnErrorResponseBase &
  OnEventResponseSubmitFeedback;
export type OnErrorResponseSubmitTicket = OnErrorResponseBase &
  OnEventResponseSubmitTicket;
export type OnErrorResponseGetSurveyResponsesForLiveChat = OnErrorResponseBase &
  OnEventResponseGetSurveyResponsesForLiveChat;

/**
 * TRACK EVENT RESPONSE TYPES
 */
export interface OnTrackEventResponse {
  eventName: string;
  additionalData?: Record<string, unknown>;
}

/**
 * LIVE CHAT PROPS
 */
export interface IngressPreEngagementData {
  friendlyName?: string;
  email?: string;
  userSid?: string;
  accountSid?: string;
  identity?: string;
}

export interface LiveChatPreEngagementData extends IngressPreEngagementData {
  location: string;
  metadata: string;
  // TODO: We should change this to a constant/enum based on available values
  productCategory: string;
  question: string;
  url: string;
}

export interface SghcLiveChatPreEngagementData
  extends LiveChatPreEngagementData {
  subProduct: string;
  username?: string;
}

// this is an an object that's stringified and passed at preEngagementData.metadata
// this is a freeform object that we control the schema for
export interface LiveChatMetadata {
  account?: LiveChatAccount;
  user?: LiveChatUser;
  customFields?: Record<number, string | number>;
  tags?: string[];
  // data from HCA chat relevant for live chat CSAT survey
  hcaCsatProps?: HcaCsatProps;
}

interface LiveChatAccount {
  sid: string;
}

interface LiveChatUser {
  sid: string;
  email: string;
  fullName: string;
}

interface HcaCsatProps {
  chatId: string;
  chatTimestamp: string;
  ingress: WiseOwlIngress;
  firstName: string;
  lastName: string;
}

export interface buildLiveChatPreEngagementDataProps {
  ingressPreEngagementData: IngressPreEngagementData;
  productCategory: string;
  question: string;
  additionalMetadata?: LiveChatMetadata;
  subProduct?: string;
  username?: string;
  ingress: WiseOwlIngress;
}

export interface HandleDirectChatCreationArgs {
  productCategory: string;
  question: string;
  ingressPreEngagementData: IngressPreEngagementData;
  subProduct?: string;
}

export interface HandleLiveChatTransferArgs
  extends HandleDirectChatCreationArgs {
  chatId: string;
}

/**
 * WISE OWL PROPS
 */
export interface WiseOwlBaseProps {
  /**
   * Display name of the assistant to use for the chat (e.g.
   * "Help Center Assistant", "Support Co-Pilot", "Intelligent Discovery")
   */
  displayName: string;
  chatId?: string;
  /**
   * String of custom CSS styles passed to the top-level WO container
   * via styled components
   */
  customStyles?: string;
  /**
   * Optional object for configuring the Wise Owl live chat integration
   */
  liveChatConfiguration?: LiveChatConfiguration;
  /**
   * Optional callback function that will receive error events from
   * the WiseOwl component
   */
  onError?: (args: OnErrorResponse) => void;
  /**
   * Optional callback that will receive event based responses from
   * all WiseOwl component events
   */
  onEvent?: (args: OnEventResponse) => void;
  /**
   * Optional callback that can be used for tracking specific events
   * (differs from onEvent in that it is based on user interaction)
   */
  onTrackEvent?: (args: OnTrackEventResponse) => void;

  /**
   * Optional callback exposes Wise Owl context to assistants that wish to
   * set up custom business logic based on the context e.g. setting up external
   * chat start triggers
   */
  onContextInit?: (context: WiseOwlContextProps) => void;

  /**
   * Enables setting of scroll container when using something other
   * than the default element (WISE_OWL_CHAT_LOG_CONTAINER)
   */
  scrollContainerElementId?: WiseOwlElementIdsType;
  /**
   * When a surveyId is provided, this enables the survey experience
   */
  surveyId?: string;
  // TODO: Consolidate below props into unified user object
  ingressLiveChatPreEngagementData?: IngressPreEngagementData;
  user: WiseOwlUser;
}

export interface WiseOwlConsoleProps extends WiseOwlBaseProps {
  applicationId: WiseOwlApplicationEnumType;
  ingress: WiseOwlIngress.console;
  // Nevers
  externalUserMessage?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
  token?: never;
}

export interface WiseOwlOscAuthenticatedProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.osc;
  // Nevers
  applicationId?: never;
  externalUserMessage?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
  token?: never;
}

export interface WiseOwlOscUnauthenticatedProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.oscUngated;
  stargateUngatedUserId: string;
  // Nevers
  applicationId?: never;
  externalUserMessage?: never;
  ticket?: never;
  token?: never;
}

export interface WiseOwlSalesforceProps extends WiseOwlBaseProps {
  externalUserMessage?: string;
  ingress: WiseOwlIngress.salesforce;
  token: string;
  // Nevers
  applicationId?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlAppProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.wiseOwlApp;
  token: string;
  externalUserMessage?: string;
  applicationId?: WiseOwlApplicationEnumType;
  // Nevers
  stargateUngatedUserId?: never;
  ticket?: never;
}

export interface WiseOwlZendeskProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.zendesk;
  ticket: ZendeskSidebarTicket;
  token: string;
  // Nevers
  applicationId?: never;
  externalUserMessage?: never;
  stargateUngatedUserId?: never;
}

export interface WiseOwlSendGridProps extends WiseOwlBaseProps {
  ingress: WiseOwlIngress.sendGrid;
  token: string;
  // Nevers
  applicationId?: never;
  externalUserMessage?: never;
  stargateUngatedUserId?: never;
  ticket?: never;
}

export type WiseOwlProps =
  | WiseOwlConsoleProps
  | WiseOwlOscAuthenticatedProps
  | WiseOwlOscUnauthenticatedProps
  | WiseOwlSalesforceProps
  | WiseOwlAppProps
  | WiseOwlZendeskProps
  | WiseOwlSendGridProps;
